import React from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Label } from '../ui/label';
import { Input } from '../ui/input';
import { Button } from '../ui/button';
import { Switch } from '../ui/switch';
import { Loader2, Save } from 'lucide-react';
import { EmailTemplatesManager } from './EmailTemplatesManager';

/** SMTP / Resend / SendGrid email provider settings. */
export function EmailTab({ smtp, setSmtp, saveSmtp, testSmtp, saving }) {
  return (
    <div className="space-y-6">
      <Card className="bg-[#041A3B] border-white/10">
      <CardHeader>
        <CardTitle className="text-white">Email Configuration</CardTitle>
        <CardDescription className="text-[#A0B4CC]">Configure SMTP or email API provider for sending notifications</CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="flex items-center justify-between p-4 rounded-lg bg-[#000B18]">
          <div>
            <Label className="text-white">Enable Email Sending</Label>
            <p className="text-[#5C7599] text-xs mt-1">When disabled, emails are logged but not sent</p>
          </div>
          <Switch checked={smtp.is_enabled} onCheckedChange={(val) => setSmtp({ ...smtp, is_enabled: val })} />
        </div>

        <div className="space-y-2">
          <Label className="text-white">Provider</Label>
          <select value={smtp.provider} onChange={(e) => setSmtp({ ...smtp, provider: e.target.value })} className="w-full h-10 px-3 rounded-md bg-[#000B18] border border-white/20 text-white" data-testid="smtp-provider">
            <option value="smtp">SMTP Server</option>
            <option value="resend">Resend</option>
            <option value="sendgrid">SendGrid</option>
          </select>
        </div>

        {smtp.provider === 'smtp' ? (
          <div className="space-y-4">
            <div className="grid md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <Label className="text-white">SMTP Host</Label>
                <Input value={smtp.host || ''} onChange={(e) => setSmtp({ ...smtp, host: e.target.value })} className="bg-[#000B18] border-white/20 text-white" placeholder="smtp.gmail.com" data-testid="smtp-host" />
              </div>
              <div className="space-y-2">
                <Label className="text-white">Port</Label>
                <Input type="number" value={smtp.port} onChange={(e) => setSmtp({ ...smtp, port: parseInt(e.target.value) || 587 })} className="bg-[#000B18] border-white/20 text-white" data-testid="smtp-port" />
              </div>
            </div>
            <div className="grid md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <Label className="text-white">Username</Label>
                <Input value={smtp.username || ''} onChange={(e) => setSmtp({ ...smtp, username: e.target.value })} className="bg-[#000B18] border-white/20 text-white" placeholder="your@email.com" data-testid="smtp-username" />
              </div>
              <div className="space-y-2">
                <Label className="text-white">Password</Label>
                <Input type="password" value={smtp.password || ''} onChange={(e) => setSmtp({ ...smtp, password: e.target.value })} className="bg-[#000B18] border-white/20 text-white" placeholder="App password..." data-testid="smtp-password" />
                {/gmail|google/i.test(smtp.host || '') && (
                  <p className="text-[#FFEA00] text-xs" data-testid="smtp-gmail-app-password-hint">
                    Gmail needs a 16-character App Password (Google Account → Security → 2-Step Verification → App passwords). Your normal Gmail password will be rejected.
                  </p>
                )}
              </div>
            </div>
          </div>
        ) : (
          <div className="space-y-2">
            <Label className="text-white">{smtp.provider === 'resend' ? 'Resend' : 'SendGrid'} API Key</Label>
            <Input type="password" value={smtp.api_key || ''} onChange={(e) => setSmtp({ ...smtp, api_key: e.target.value })} className="bg-[#000B18] border-white/20 text-white" placeholder={smtp.provider === 'resend' ? 're_...' : 'SG...'} data-testid="smtp-api-key" />
          </div>
        )}

        <div className="grid md:grid-cols-2 gap-6">
          <div className="space-y-2">
            <Label className="text-white">From Email</Label>
            <Input value={smtp.from_email} onChange={(e) => setSmtp({ ...smtp, from_email: e.target.value })} className="bg-[#000B18] border-white/20 text-white" data-testid="smtp-from-email" />
          </div>
          <div className="space-y-2">
            <Label className="text-white">From Name</Label>
            <Input value={smtp.from_name} onChange={(e) => setSmtp({ ...smtp, from_name: e.target.value })} className="bg-[#000B18] border-white/20 text-white" data-testid="smtp-from-name" />
          </div>
          <div className="space-y-2 md:col-span-2">
            <Label className="text-white">Reply-To Email</Label>
            <Input type="email" value={smtp.reply_to || ''} onChange={(e) => setSmtp({ ...smtp, reply_to: e.target.value })} placeholder="support@allglobalaccess.com" className="bg-[#000B18] border-white/20 text-white" data-testid="smtp-reply-to" />
            <p className="text-[#5C7599] text-xs">When recipients hit "Reply", their message goes here instead of the From address. Applies to every email the system sends. Leave blank to use the From address.</p>
          </div>
        </div>

        {!smtp.is_enabled && (
          <div className="p-3 rounded-lg bg-[#FFEA00]/5 border border-[#FFEA00]/20">
            <p className="text-[#FFEA00] text-xs">Email sending is disabled. Notifications are logged to the database but not delivered. Configure and enable to send real emails.</p>
          </div>
        )}

        <div className="flex gap-3">
          <Button onClick={saveSmtp} disabled={saving} className="btn-primary" data-testid="save-smtp-btn">
            {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
            Save Email Settings
          </Button>
          <Button onClick={testSmtp} variant="outline" className="border-white/20 text-white hover:bg-white/5" data-testid="test-smtp-btn">
            Send Test Email
          </Button>
        </div>
      </CardContent>
    </Card>

    <EmailTemplatesManager />
    </div>
  );
}
